import React from 'react';
import { Modal, Text, Button, Stack, Group, Select, Box, useComputedColorScheme, useMantineTheme } from '@mantine/core';
import { Menu } from '../../types/menu';
import { t } from '../../utils/uiTranslations';

interface OnboardingModalProps {
  opened: boolean;
  onClose: () => void;
  menu: Menu;
  currentLanguage?: string;
  onLanguageChange?: (language: string) => void;
  onQuestionClick?: (question: string) => void;
}

/**
 * Default onboarding content (language-aware).
 * This is used only if the menu does not provide onboardingContent for the selected language.
 */
const getDefaultOnboardingContent = (currentLanguage: string) => ({
  title: t('onboardingTitle', currentLanguage),
  sections: [
    {
      title: t('onboardingSectionTitle', currentLanguage),
      content: t('onboardingSectionContent', currentLanguage)
    }
  ],
  buttonText: t('onboardingButtonText', currentLanguage),
  questionChips: [
    t('defaultQuestion1', currentLanguage),
    t('defaultQuestion2', currentLanguage),
    t('defaultQuestion3', currentLanguage),
  ]
});

// Helper function to get effective onboarding content based on language
export const getEffectiveOnboardingContent = (menu: Menu, currentLanguage: string = 'en') => {
  const defaults = getDefaultOnboardingContent(currentLanguage);

  // If menu has language-specific onboarding content
  if (menu.onboardingContent?.languageConfigs?.[currentLanguage]) {
    const languageConfig = menu.onboardingContent.languageConfigs[currentLanguage];
    return {
      title: languageConfig.title || defaults.title,
      sections: languageConfig.sections || defaults.sections,
      buttonText: languageConfig.buttonText || defaults.buttonText,
      questionChips: languageConfig.questionChips || defaults.questionChips
    };
  }

  // If menu has fallback configuration
  if (menu.onboardingContent?.fallbackConfig) {
    return {
      title: menu.onboardingContent.fallbackConfig.title || defaults.title,
      sections: menu.onboardingContent.fallbackConfig.sections || defaults.sections,
      buttonText: menu.onboardingContent.fallbackConfig.buttonText || defaults.buttonText,
      questionChips: menu.onboardingContent.fallbackConfig.questionChips || defaults.questionChips
    };
  }

  // Otherwise use default content
  return defaults;
};

// Styles for question chips - now a function to support dark mode
const getQuestionChipStyles = (isDarkMode: boolean) => ({
  questionChips: {
    display: 'flex' as const,
    flexWrap: 'wrap' as const,
    gap: '8px',
    marginTop: '16px',
    marginLeft: '4px'
  },

  questionChip: {
    cursor: 'pointer',
    transition: 'all 0.2s ease',
  },
  questionChipHover: {
    transform: 'translateY(-1px)',
    // Conditional shadow for dark mode - white glow for better visibility
    boxShadow: isDarkMode 
      ? '0 2px 4px rgba(255, 255, 255, 0.15), 0 1px 2px rgba(0, 0, 0, 0.2)' // White glow + dark shadow
      : '0 2px 4px rgba(0,0,0,0.1)', // Original shadow for light mode
  }
});

export const OnboardingModal: React.FC<OnboardingModalProps> = ({
  opened,
  onClose,
  menu,
  currentLanguage = 'en',
  onLanguageChange,
  onQuestionClick,
}) => {
  const theme = useMantineTheme();
  const colorScheme = useComputedColorScheme('light', { getInitialValueInEffect: true });
  const isDarkMode = colorScheme === 'dark';
  const content = getEffectiveOnboardingContent(menu, currentLanguage);
  const questionChipStyles = getQuestionChipStyles(isDarkMode);
  const onboardingImageAspectRatio = theme.other?.layout?.onboardingImageAspectRatio ?? '4 / 3';
  const onboardingImageMaxHeight = theme.other?.layout?.onboardingImageMaxHeight ?? 240;

  // Replace restaurant name placeholder in title
  const title = content.title.replace(
    '{restaurantName}',
    menu.restaurant.name || t('onboardingFallbackRestaurant', currentLanguage)
  );

  const handleQuestionClick = (question: string) => {
    // 1. Close the modal
    onClose();
    // 2. Send the question (this will open the messenger and send the message)
    onQuestionClick?.(question);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={<Text size="xl" fw={700}>{title}</Text>}
      size="lg"
      centered
    >
      <Stack gap="md">
        {menu.onboardingModalImageUrl && (
          <Box
            style={{
              position: 'relative',
              width: '100%',
              overflow: 'hidden',
              borderRadius: '8px',
              // Keep onboarding artwork consistently framed across devices.
              aspectRatio: onboardingImageAspectRatio,
              maxHeight: onboardingImageMaxHeight,
            }}
          >
            <img
              src={menu.onboardingModalImageUrl}
              alt={`${menu.restaurant.name || 'Restaurant'} onboarding`}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                display: 'block',
              }}
            />
          </Box>
        )}

        {content.sections.map((section, index) => (
          <Text key={index}>
            {section.title && <strong>{section.title}</strong>}
            {section.title && <br />}
            {section.content}
          </Text>
        ))}

        {onLanguageChange && (
          <Select
            label={t('selectLanguageTitle', currentLanguage)}
            value={currentLanguage}
            data={[
              { value: 'en', label: 'English' },
            ]}
            onChange={(value) => {
              if (value) {
                onLanguageChange(value);
              }
            }}
          />
        )}

        {/* Question chips - only show if onQuestionClick is provided */}
        {onQuestionClick && (
          <div style={questionChipStyles.questionChips}>
            {content.questionChips.map((question, index) => (
              <Button
                key={index}
                variant="light"
                size="xs"
                style={questionChipStyles.questionChip}
                onMouseEnter={(e) => {
                  Object.assign(e.currentTarget.style, questionChipStyles.questionChipHover);
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = '';
                  e.currentTarget.style.boxShadow = '';
                }}
                onClick={() => handleQuestionClick(question)}
              >
                {question}
              </Button>
            ))}
          </div>
        )}

        <Group justify="center" mt="md" w="100%">
          <Button onClick={onClose} fullWidth>
            {content.buttonText}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}; 